/* ==========================================================================
   MINAROMINA HEADER FUNCTIONALITY
   Mobile menu and header interactions
   ========================================================================== */

class MinaRominaHeader {
  constructor() {
    this.header = document.querySelector('.mm-header');
    this.mobileToggle = document.querySelector('.mm-header__mobile-toggle');
    this.mobileMenu = document.querySelector('.mm-mobile-menu');
    this.mobileClose = document.querySelector('.mm-mobile-menu__close');
    this.mobileOverlay = document.querySelector('.mm-mobile-menu__overlay');

    this.init();
  }

  init() {
    if (!this.header) return;

    this.setupMobileMenu();
    this.setupScrollBehavior();
    this.setupAccessibility();
  }

  setupMobileMenu() {
    if (!this.mobileToggle || !this.mobileMenu) return;

    // Open mobile menu
    this.mobileToggle.addEventListener('click', () => {
      this.openMobileMenu();
    });

    // Close mobile menu
    if (this.mobileClose) {
      this.mobileClose.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }

    // Close on overlay click
    if (this.mobileOverlay) {
      this.mobileOverlay.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMobileMenuOpen()) {
        this.closeMobileMenu();
      }
    });

    // Close on mobile menu link click
    const mobileLinks = this.mobileMenu.querySelectorAll('.mm-mobile-menu__link');
    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.closeMobileMenu();
      });
    });
  }

  openMobileMenu() {
    this.mobileMenu.classList.add('mm-mobile-menu--open');
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mm-mobile-menu-active');

    // Focus first menu item
    const firstLink = this.mobileMenu.querySelector('.mm-mobile-menu__link');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 100);
    }
  }

  closeMobileMenu() {
    this.mobileMenu.classList.remove('mm-mobile-menu--open');
    this.mobileToggle.setAttribute('aria-expanded', 'false');
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mm-mobile-menu-active');

    // Return focus to toggle button
    this.mobileToggle.focus();
  }

  isMobileMenuOpen() {
    return this.mobileMenu.classList.contains('mm-mobile-menu--open');
  }

  setupScrollBehavior() {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
      const currentScrollY = window.scrollY;

      // Add scrolled class when not at top
      if (currentScrollY > 10) {
        this.header.classList.add('mm-header--scrolled');
      } else {
        this.header.classList.remove('mm-header--scrolled');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
  }

  setupAccessibility() {
    // Trap focus in mobile menu when open
    const trapFocus = (e) => {
      if (!this.isMobileMenuOpen()) return;

      const focusableElements = this.mobileMenu.querySelectorAll(
        'a[href], button, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', trapFocus);

    // Announce cart count changes
    const cartIcon = this.header.querySelector('.mm-header__icon--cart');
    if (cartIcon) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.classList.contains('mm-header__cart-count')) {
            const count = mutation.target.textContent;
            this.announceCartUpdate(count);
          }
        });
      });

      const cartCount = cartIcon.querySelector('.mm-header__cart-count');
      if (cartCount) {
        observer.observe(cartCount, { childList: true, characterData: true });
      }
    }
  }

  announceCartUpdate(count) {
    // Create temporary announcement for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.classList.add('visually-hidden');
    announcement.textContent = `Cart updated. ${count} item${count !== '1' ? 's' : ''} in cart.`;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MinaRominaHeader();
  });
} else {
  new MinaRominaHeader();
}

// Export for external use
window.MinaRominaHeader = MinaRominaHeader;